import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Roles that can appear as pool members / hour contributors.
const ALL_POOL_ROLES = [
  { key: "server", label: "Server" },
  { key: "bar", label: "Bar / Bartender" },
  { key: "busser", label: "Busser" },
  { key: "runner", label: "Food Runner" },
  { key: "host", label: "Host" },
  { key: "cashier", label: "Cashier" },
];

export default function TipPoolCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    axios.get(`${API}/tip-pool/settings`)
      .then((r) => setSettings(r.data))
      .catch(() => toast.error("Couldn't load tip pool settings"));
  }, []);

  const runReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await axios.get(`${API}/tip-pool/report`, { params: { day } });
      setReport(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report failed");
    } finally {
      setReportLoading(false);
    }
  };

  const previewWithCurrent = async () => {
    setReportLoading(true);
    try {
      const { data } = await axios.post(`${API}/tip-pool/preview`, { ...settings, day });
      setReport(data);
      toast.success("Preview updated with unsaved settings");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setReportLoading(false);
    }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/store-settings`, {
        tip_pool_enabled: !!settings.tip_pool_enabled,
        tip_pool_mode: settings.tip_pool_mode,
        tip_pool_contribution_percent: Number(settings.tip_pool_contribution_percent),
        tip_pool_role_percents: settings.tip_pool_role_percents,
        tip_pool_hours_roles: settings.tip_pool_hours_roles,
      });
      toast.success("Tip pool rules saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Card><CardContent className="py-6 text-muted-foreground text-sm">Loading…</CardContent></Card>;

  const rolePercents = settings.tip_pool_role_percents || {};
  const totalRolePct = Object.values(rolePercents).reduce((s, v) => s + (Number(v) || 0), 0);
  const hoursRoles = settings.tip_pool_hours_roles || [];

  const setRolePct = (key, val) => {
    setSettings((prev) => ({
      ...prev,
      tip_pool_role_percents: { ...(prev.tip_pool_role_percents || {}), [key]: Number(val) || 0 },
    }));
  };
  const toggleHoursRole = (key) => {
    setSettings((prev) => {
      const cur = new Set(prev.tip_pool_hours_roles || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...prev, tip_pool_hours_roles: Array.from(cur) };
    });
  };

  return (
    <div className="space-y-4" data-testid="tip-pool-card">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Tip Pool / Sharing</CardTitle>
              <CardDescription>
                Route a portion of every tipped ticket into a shared pool and split it by clocked hours or by fixed role percentages.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Enabled</span>
              <Switch
                checked={!!settings.tip_pool_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, tip_pool_enabled: v })}
                data-testid="tip-pool-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pool mode</Label>
              <Select
                value={settings.tip_pool_mode || "hours"}
                onValueChange={(v) => setSettings({ ...settings, tip_pool_mode: v })}
              >
                <SelectTrigger data-testid="tip-pool-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hours" data-testid="tip-pool-mode-hours">Hours worked (proportional)</SelectItem>
                  <SelectItem value="role_split" data-testid="tip-pool-mode-role-split">Fixed role percentages</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contribution to pool (%)</Label>
              <Input
                type="number" min="0" max="100" step="1"
                value={settings.tip_pool_contribution_percent ?? 100}
                onChange={(e) => setSettings({ ...settings, tip_pool_contribution_percent: Number(e.target.value) })}
                data-testid="tip-pool-contribution"
              />
              <p className="text-xs text-muted-foreground">100 = whole tip goes to the pool. 30 = 30% pooled, server keeps 70%.</p>
            </div>
          </div>

          {settings.tip_pool_mode === "role_split" ? (
            <div className="space-y-3">
              <Label>Role percentages</Label>
              <div className="grid grid-cols-2 gap-3">
                {ALL_POOL_ROLES.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-32 text-sm">{label}</span>
                    <Input
                      type="number" min="0" max="100" step="1"
                      value={rolePercents[key] ?? 0}
                      onChange={(e) => setRolePct(key, e.target.value)}
                      className="w-24"
                      data-testid={`tip-pool-role-${key}`}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                ))}
              </div>
              <p className={`text-xs ${totalRolePct === 100 ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}`} data-testid="tip-pool-role-total">
                Total: {totalRolePct}% {totalRolePct !== 100 && "— rounded proportionally; ideally sum to 100"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <Label>Roles that share the pool by hours worked</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_POOL_ROLES.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`tip-pool-hours-role-${key}`}>
                    <input
                      type="checkbox"
                      checked={hoursRoles.includes(key)}
                      onChange={() => toggleHoursRole(key)}
                      className="rounded"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={previewWithCurrent} data-testid="tip-pool-preview">
              Preview (unsaved)
            </Button>
            <Button onClick={save} disabled={saving} data-testid="tip-pool-save">
              {saving ? "Saving…" : "Save rules"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Distribution report</CardTitle>
              <CardDescription>See how a shift&apos;s tips would divide under the current rules.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-40" data-testid="tip-pool-report-day" />
              <Button variant="secondary" onClick={runReport} disabled={reportLoading} data-testid="tip-pool-report-run">
                {reportLoading ? "…" : "Run"}
              </Button>
              <button
                type="button"
                onClick={() => {
                  import("@/utils/downloadCsv").then((m) => {
                    m.downloadCsv(`${API}/tip-pool/report/export.csv?day=${day}`);
                  });
                }}
                className="text-xs text-primary hover:underline"
                data-testid="tip-pool-report-export-csv"
              >
                Export CSV
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!report ? (
            <p className="text-sm text-muted-foreground">Pick a date and click Run.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg bg-secondary/40 p-3">
                  <p className="text-xs text-muted-foreground">Total tips</p>
                  <p className="font-heading text-lg">${report.total_tips?.toFixed(2)}</p>
                </div>
                <div className="rounded-lg bg-secondary/40 p-3">
                  <p className="text-xs text-muted-foreground">Into pool</p>
                  <p className="font-heading text-lg" data-testid="tip-pool-total">${report.pool_total?.toFixed(2)}</p>
                </div>
                <div className="rounded-lg bg-secondary/40 p-3">
                  <p className="text-xs text-muted-foreground">Kept by servers</p>
                  <p className="font-heading text-lg">${report.kept_by_server_total?.toFixed(2)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Payout</p>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left py-1">Name</th>
                      <th className="text-left py-1">Role</th>
                      <th className="text-right py-1">Hours</th>
                      <th className="text-right py-1">Share</th>
                      <th className="text-right py-1">$ Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.distribution || []).map((row, idx) => (
                      <tr key={idx} className="border-t border-border" data-testid={`tip-pool-row-${idx}`}>
                        <td className="py-1">{row.user_name || "—"}</td>
                        <td className="py-1 text-muted-foreground">{row.role || "—"}</td>
                        <td className="py-1 text-right">{row.hours?.toFixed(2)}</td>
                        <td className="py-1 text-right">{row.percent?.toFixed(2)}%</td>
                        <td className="py-1 text-right font-medium">${row.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
